import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { GenericGridSmartViewsWebComponent } from '../../../grid/genericGridSmartViews';
import { GzPushBodyLayoutWebComponent } from '../../../ui/gzPushBodyLayout';
import { GridViewWebComponent } from '../../../grid/gridView';
import { Column, ColumnKeys, CustomRuleDetails, Dependencies, ResponseGridViewData, Status } from '../types';
import {
  WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
} from '../../../webcomponents.constants';
import {
  CategoriesConfig,
  convertSmartViewToGridView,
  SmartViewItemConfiguration,
} from '../../../grid/genericGridSmartViews/types';
import { ColumnDetails } from '../../../grid/types/common';
import { ResponseGridDataProvider } from '../dataProvider/ResponseGridDataProvider';
import { GridTitleConfig } from '../../../grid/uiElements/gridViewTitle/types';
import { translateFn } from '../../../utils/translator.helper';
import _ from 'lodash';
import { TemplateState } from '../../../types/common/types';
import {
  detailsRenderer,
  executedByRenderer,
  executedOnRenderer,
  objectRenderer,
  updateStatusActionsRenderer,
} from './customRenderers';
import { SelectOption } from '../../../ui/gzSelect/types';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import { CustomRulesSidePanelWebComponent } from '../../../customRules/customRulesSidePanel';
import { GridType } from '../../../customRules/customRulesGrid/types';

export class ResponseGridView extends BaseWebComponentView {
  public dom: {
    $pushBodyLayout: GzPushBodyLayoutWebComponent;
    $smartViewsTitle: GzTitleWebComponent;
    $smartViews: GenericGridSmartViewsWebComponent;
    $smartViewsLoader: HTMLElement;
    $gridWrapper: HTMLElement;
    $gridView: GridViewWebComponent<Column, ColumnKeys>;
    $customRuleDetailsSidePanel: GzSideMenuWebComponent;
  };

  protected dependencies: Dependencies;

  private smartViewsStrings: object;
  private incidentId: string;
  private allColumns: Array<ColumnDetails<Column, ColumnKeys>>;
  private predefinedFilterKeys: Array<ColumnKeys>;
  private allUpdateStatusActions: Array<SelectOption>;

  public getTranslations(): void {
    this.translations = {
      updateStatusActionSuccessMessages: {
        [Status.DISMISSED]: translateFn('Response action dismissed'),
        [Status.DONE_EXTERNALLY]: translateFn('Response action marked as done'),
      },
      updateStatusActionFailedMessage: translateFn('Status change failed'),
      customRuleDetailsFailedToLoadMessage: translateFn('Custom rule is not available anymore or failed to load'),
    };

    this.smartViewsStrings = this.stringsTranslator.getSmartViewsTranslations();
  }

  public setTranslatedStrings(): void {
    this.dom.$smartViewsTitle.innerHTML = this.smartViewsStrings['genericPanelTitle'].toUpperCase();
  }

  public async getDomElements(): Promise<void> {
    this.dom.$pushBodyLayout = this.shadow.querySelector(WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT);
    this.dom.$smartViewsTitle = this.shadow.querySelector('#edr-response-smart-views-title');
    this.dom.$smartViews = this.shadow.querySelector(WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS);
    this.dom.$smartViewsLoader = this.shadow.querySelector('#edr-response-smart-views-loader');
    this.dom.$gridWrapper = this.shadow.querySelector('.wrapper');
    this.dom.$gridView = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW);
    this.dom.$customRuleDetailsSidePanel = this.shadow.querySelector('#custom-rule-details');

    this.getTranslations();
    this.setTranslatedStrings();
  }

  public async buildSuccessView(data: ResponseGridViewData): Promise<void> {
    this.incidentId = data.incidentId;
    this.allColumns = data.allColumns;
    this.predefinedFilterKeys = data.predefinedFilterKeys;
    this.allUpdateStatusActions = data.allUpdateStatusActions;

    this.resetTemplateState(this.dom.$gridWrapper);

    await this.buildSmartViews(data.smartViewsCategories);
    await this.buildGridView(this.dom.$smartViews.controller?.getActiveView() as SmartViewItemConfiguration);
  }

  public async buildGridView(smartView: SmartViewItemConfiguration): Promise<void> {
    this.dom.$gridView.propertiesSet = false;

    this.dom.$gridView.dataProvider = new ResponseGridDataProvider(this.incidentId);

    const gridView = convertSmartViewToGridView<Column, ColumnKeys>(smartView), initialGridView = _.cloneDeep(gridView);

    this.dom.$gridView.titleConfig = new GridTitleConfig(gridView.name);
    this.dom.$gridView.allColumnDetails = this.allColumns;
    this.dom.$gridView.columnRenderers = {
      [ColumnKeys.OBJECT]: objectRenderer.bind(this),
      [ColumnKeys.DETAILS]: detailsRenderer.bind(this),
      [ColumnKeys.UPDATE_STATUS_ACTIONS]: updateStatusActionsRenderer.bind(this),
      [ColumnKeys.EXECUTED_ON]: executedOnRenderer.bind(this),
      [ColumnKeys.EXECUTED_BY]: executedByRenderer.bind(this),
    };
    this.dom.$gridView.columns = gridView.displayedColumns;
    this.dom.$gridView.columnConfigs = gridView.columnConfigs ?? [];
    this.dom.$gridView.filters = gridView.filters;
    this.dom.$gridView.predefinedFiltersKeys = this.predefinedFilterKeys;
    this.dom.$gridView.viewId = gridView.id;
    this.dom.$gridView.defaultViewProps = initialGridView;
    this.dom.$gridView.activeViewProps = gridView;
    this.dom.$gridView.activeViewInitialProps = initialGridView;
    this.dom.$gridView.isCompactViewChecked = gridView.compactView;
    this.dom.$gridView.hasPagination = true;
    this.dom.$gridView.pageSize = gridView.pageSize;
    this.dom.$gridView.showGlobalActions = true;
    this.dom.$gridView.customStrings = {
      noSearchResultsString: translateFn('No response actions found'),
      dataLoadingFailed: translateFn('Failed to load response actions'),
    };

    this.dom.$gridView.propertiesSet = true;

    this.dom.$gridView.injectDependencies(this.dependencies);

    await this.dom.$gridView.start();
  }

  public buildErrorView(error: Error): void {
    this.resetTemplateState(this.dom.$gridWrapper);

    this.setTemplateState(this.dom.$gridWrapper, TemplateState.ERROR);
  }

  public showUpdateStatusActionSuccessToast(status: Status): void {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: this.translations.updateStatusActionSuccessMessages[status],
      severity: ToastSeverity.SUCCESS,
    });
  }

  public showUpdateStatusActionFailedToast(): void {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: this.translations.updateStatusActionFailedMessage,
      severity: ToastSeverity.ERROR,
    });
  }

  public openCustomRuleDetailsSidePanel(customRuleDetails: CustomRuleDetails): void {
    const sidePanel = new CustomRulesSidePanelWebComponent();

    sidePanel.rule = customRuleDetails.rule;
    sidePanel.isGZ = this.dependencies.isGZ();
    sidePanel.gridType = GridType.DETECTION;
    sidePanel.targets = customRuleDetails.networkTargets;
    sidePanel.company = this.dependencies.getActiveCompany();
    sidePanel.isPartner = this.dependencies.isPartnerCompany();
    sidePanel.showNavigation = false;
    sidePanel.showActions = false;

    this.dom.$customRuleDetailsSidePanel.setMenuContent(sidePanel);
    this.dom.$customRuleDetailsSidePanel.openSideMenu();
  }

  public showCustomRuleDetailsFailedToLoadToast(): void {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: this.translations.customRuleDetailsFailedToLoadMessage,
      severity: ToastSeverity.ERROR,
    });
  }

  private async buildSmartViews(categoriesConfig: CategoriesConfig): Promise<void> {
    this.dom.$smartViews.categoriesConfig = categoriesConfig;
    this.dom.$smartViews.injectDependencies(this.dependencies);

    await this.dom.$smartViews.start();

    this.setElementClass(this.dom.$smartViewsLoader, 'hidden');
    this.removeElementClass(this.dom.$smartViews, 'hidden');
  }
}
